import { z } from 'zod';
import { ElementBindingsSchema } from './binding.schema';
import { ElementPropsSchema } from './element-props.schema';

// A page as a tree of elements the merchant owns outright, rather than a list of bands whose insides
// only their theme can name. Every node is selectable, stylable, and can hold other nodes — a countdown
// beside an icon inside a column inside a row is spelled the same way at every depth.

/** How deep a tree may nest, and how many nodes it may hold, so a page can never be unbounded. */
export const MAX_ELEMENT_DEPTH = 12;
export const MAX_ELEMENT_NODES = 2000;
export const MAX_ELEMENT_CHILDREN = 200;
/** A whole tree, serialised. A prop value is `unknown`, so without this one node could carry a megabyte. */
export const MAX_ELEMENT_BYTES = 900_000;

export const ELEMENT_TYPES = [
  // Structure — the boxes everything else sits in.
  'section', 'row', 'column', 'container', 'slideshow', 'marquee', 'tabs', 'accordion',
  // Text.
  'heading', 'paragraph', 'list',
  // Media.
  'image', 'icon', 'video', 'audio',
  // Forms and actions.
  'button', 'link', 'block-link', 'input', 'textarea', 'select', 'checkbox', 'label',
  'stars', 'file-upload', 'form',
  // Everything else.
  'spacer', 'divider', 'custom-html',
  // The store's own — what belongs to the shop rather than to one page.
  'menu', 'cart', 'search', 'collection-list', 'product-list', 'product-scope', 'bundle', 'purchase', 'checkout', 'order-summary', 'offer',
  'breadcrumbs', 'product-media', 'social-links', 'review-list', 'popup',
  // A product's colours as round swatches, as Shopify themes print them on a card.
  'swatches',
  // A collection or search page's listing: its filters, its count and sort, its pages.
  'listing-filters', 'listing-toolbar', 'listing-pagination',
  // The bar that rises from the bottom of a product page once its buy box is out of sight.
  'sticky-buy',
  // The cart page's own content: its lines, note, total and Check out.
  'cart-contents', 'order-lookup'
] as const;

export const ElementTypeEnum = z.enum(ELEMENT_TYPES);

/** The widths a style may differ at. `base` is the design; the others override it downwards. */
export const BREAKPOINTS = ['base', 'tablet', 'mobile'] as const;
export const BreakpointEnum = z.enum(BREAKPOINTS);

/** The states a style may differ in. Anything beyond these belongs in the theme's own stylesheet. */
export const ELEMENT_STATES = ['default', 'hover', 'focus', 'active'] as const;
export const ElementStateEnum = z.enum(ELEMENT_STATES);

/**
 * Every style property an element may carry. A closed list on purpose: it is the vocabulary the
 * inspector draws controls from, so a property nothing can edit cannot be stored, and a stylesheet
 * cannot grow a property no merchant can find again.
 */
export const STYLE_PROPERTIES = [
  'display', 'flexDirection', 'flexWrap', 'justifyContent', 'alignItems', 'alignSelf', 'gap',
  // The two halves of the gap, after the shorthand. LightFunnels' Row panel sets them apart, and it
  // has to: `shareWidth` subtracts the horizontal gutter out of every column's twelfth, so that one is
  // load-bearing arithmetic while the space between wrapped lines is free to be anything.
  'columnGap', 'rowGap',
  'gridColumns', 'order', 'flexGrow', 'flexShrink',
  'marginTop', 'marginRight', 'marginBottom', 'marginLeft',
  // Logical, so a band laid out for Arabic mirrors itself instead of needing a second copy of the page.
  'marginInlineStart', 'marginInlineEnd', 'paddingInlineStart', 'paddingInlineEnd',
  'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
  'width', 'minWidth', 'maxWidth', 'height', 'minHeight', 'maxHeight', 'aspectRatio',
  'color', 'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'lineHeight', 'letterSpacing',
  'textAlign', 'textTransform', 'textDecoration', 'whiteSpace', 'textOverflow',
  'backgroundColor', 'backgroundImage', 'backgroundSize', 'backgroundPosition', 'backgroundRepeat',
  'borderStyle', 'borderWidth', 'borderColor',
  // Per-side widths after the shorthand, so a one-edge border wins the write order.
  'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
  'borderTopLeftRadius', 'borderTopRightRadius', 'borderBottomRightRadius', 'borderBottomLeftRadius',
  'boxShadow', 'textShadow', 'opacity', 'overflow', 'objectFit', 'cursor',
  'position', 'top', 'right', 'bottom', 'left', 'zIndex',
  'transition', 'transform',
  // A motion the element runs on its own. A transition only plays between two states; a ticker sliding
  // its promises across a bar plays forever, and that is the one thing a bar of basic elements could
  // not do. The keyframes it names are the engine's, in `elements.css`, so a merchant picks a motion.
  'animation'
] as const;

export const StylePropertyEnum = z.enum(STYLE_PROPERTIES);

const UNSAFE_STYLE_RE = /expression\s*\(|javascript:|<\/?script|@import|behavior\s*:|url\s*\(\s*["']?\s*(?:javascript|data:text\/html)/i;

/**
 * Reports whether a style value is one a page may carry. Exported because the builder compiles a tree
 * before the server has ever seen it, and a second copy of this rule in the browser is a second answer
 * to "is this safe" — the two would drift, and the browser's copy is the one a merchant reaches first.
 */
export const isSafeStyleValue = (value: string): boolean => !UNSAFE_STYLE_RE.test(value);

/**
 * One style value: a literal, or a reference to one of the store's named design tokens. The token form
 * is what makes a palette worth having — a heading bound to `title` follows the store when it repaints,
 * while a literal stays exactly where the merchant put it.
 */
export const StyleValueSchema = z.union([
  z.string().max(400).refine(isSafeStyleValue, 'That value is not allowed in a style'),
  z.number().finite(),
  /**
   * A named token, optionally at a fraction of its opacity. Every theme tints something with its own
   * accent — an overlay over a photo, an underline, a soft panel — and without `alpha` a preset can only
   * spell that as a literal, which then stops following the store when its palette is repainted.
   */
  z.object({ token: z.string().min(1).max(60), alpha: z.number().min(0).max(1).optional() }).strict()
]);

/** The properties set on one element, at one breakpoint, in one state. */
export const StyleBlockSchema = z.record(StylePropertyEnum, StyleValueSchema);

/** Everything an element looks like: per state, per width. An absent entry inherits from `base`/`default`. */
export const ElementStylesSchema = z.record(
  BreakpointEnum,
  z.record(ElementStateEnum, StyleBlockSchema)
);

/**
 * An element's own content and behaviour, as distinct from how it looks. It is walked by the very same
 * grammar that guards a section's settings — the one that refuses `<script`, `javascript:` and
 * `data:text/html` in any string, at any depth. A text or `custom-html` prop is merchant input
 * heading for a shopper's page, so it cannot be the one shape in the system that travels unchecked.
 */
export { ElementPropsSchema };

const ELEMENT_ID_RE = /^[a-zA-Z0-9_-]{1,64}$/;

/**
 * A merchant's own hook onto one element: the class their stylesheet targets, and the id their script or
 * their anchor link points at. Every LightFunnels element carries both and ours carried neither, which
 * left a merchant with no way to reach one element from outside the builder.
 *
 * The charset is narrow on purpose. These land in real attributes, so anything that could close one and
 * open another is refused here rather than escaped later: a class is space-separated CSS identifiers, an
 * id is one of them. Both must start with a letter, which is what keeps a bare number out of a selector.
 */
const CSS_NAME = '[A-Za-z][A-Za-z0-9_-]*';
export const ELEMENT_CLASS_RE = new RegExp(`^${CSS_NAME}(?: ${CSS_NAME})*$`);
export const ELEMENT_DOM_ID_RE = new RegExp(`^${CSS_NAME}$`);

/** How long a merchant's own class list or id may be; far past anything a hand writes. */
export const MAX_CSS_NAME_LENGTH = 120;

/** The longest a merchant may name a band before the tree has to truncate it. Matches a block's own. */
export const MAX_ELEMENT_NAME = 80;

/** One node's id, spelled once so the builder's channel and the stored tree cannot disagree about it. */
export const ElementIdSchema = z.string().regex(ELEMENT_ID_RE, 'An element id is letters, digits, dashes and underscores');

/**
 * A path into one element's props, exactly as the inspector writes it: dotted keys, and a list index as
 * its own segment — `text`, `block.padding.top`, `items.2.label`. It is what lets a merchant click the
 * words themselves rather than the box they sit in.
 */
export const FieldPathSchema = z.string().max(200).regex(/^[a-zA-Z0-9_]+(\.[a-zA-Z0-9_]+)*$/, 'A field path is dotted keys and list indices');

/** One node of the tree. Recursive, so the same shape describes a section and the icon inside it. */
export type ElementNodeDto = {
  id: string;
  type: z.infer<typeof ElementTypeEnum>;
  props: Record<string, unknown>;
  styles: z.infer<typeof ElementStylesSchema>;
  children: ElementNodeDto[];
  /** Hidden at these widths, so a band can be desktop-only without a second copy of the page. */
  hiddenOn?: z.infer<typeof BreakpointEnum>[];
  /** Props the shop answers instead of the merchant: prop key to source. A bound prop is never typed. */
  bindings?: z.infer<typeof ElementBindingsSchema>;
  /** What this band is, kept from the block it was placed from, so the tree names it and not its words. */
  name?: string;
  /** The block this band was placed from, which is what its settings panel and, one day, a re-stamp read. */
  blockId?: string;
  /**
   * Which node of that block this one was copied from, since placing mints a fresh id at every level.
   * It is how a panel finds the box it writes into, and it is the ONLY thing stored for that: a setting's
   * value stays where it is drawn from — the prop itself — so a panel can never disagree with the page.
   */
  sourceId?: string;
  /** Shared with every page that uses it; editing one edits all, until it is detached. */
  sharedId?: string;
  /** The merchant's own class list, written onto the element beside the one the style engine compiles. */
  cssClass?: string;
  /** The merchant's own id, which is what an anchor link or their own script reaches this element by. */
  domId?: string;
};

/** Built once, its children reaching back to it lazily: wrapping the whole object in z.lazy rebuilt every field's schema
 * for every node checked, which was most of the quarter second a phone spent reading a home page's tree. */
export const ElementNodeSchema: z.ZodType<ElementNodeDto, z.ZodTypeDef, unknown> = z.object({
  id: z.string().regex(ELEMENT_ID_RE, 'An element id is letters, digits, dashes and underscores'),
  type: ElementTypeEnum,
  props: ElementPropsSchema.default({}),
  styles: ElementStylesSchema.default({}),
  children: z.array(z.lazy(() => ElementNodeSchema)).max(MAX_ELEMENT_CHILDREN).default([]),
  hiddenOn: z.array(BreakpointEnum).max(3).optional(),
  bindings: ElementBindingsSchema.optional(),
  name: z.string().trim().min(1).max(MAX_ELEMENT_NAME).optional(),
  blockId: z.string().min(2).max(60).optional(),
  sourceId: z.string().regex(ELEMENT_ID_RE, 'A source id is the block node this one was copied from').optional(),
  sharedId: z.string().max(64).optional(),
  cssClass: z.string().max(MAX_CSS_NAME_LENGTH).regex(ELEMENT_CLASS_RE, 'A class is letters, digits, dashes and underscores, separated by spaces').optional(),
  domId: z.string().max(MAX_CSS_NAME_LENGTH).regex(ELEMENT_DOM_ID_RE, 'An id is one word of letters, digits, dashes and underscores').optional()
}).strict();

/**
 * One page as a tree: the roots the page draws, in order.
 *
 * `version` is 2 because a version-1 node could carry a `preset` naming a theme band, a `binding` onto
 * the shop and a `visibleIf` this build has no way to answer. A page written then is not a smaller page
 * now — it is one nothing here can draw.
 */
export const ElementTreeSchema = z.object({
  version: z.literal(2),
  roots: z.array(ElementNodeSchema).max(MAX_ELEMENT_CHILDREN)
}).strict();

export type ElementType = z.infer<typeof ElementTypeEnum>;
export type Breakpoint = z.infer<typeof BreakpointEnum>;
export type ElementState = z.infer<typeof ElementStateEnum>;
export type StyleProperty = z.infer<typeof StylePropertyEnum>;
export type StyleValueDto = z.infer<typeof StyleValueSchema>;
export type StyleBlockDto = z.infer<typeof StyleBlockSchema>;
export type ElementStylesDto = z.infer<typeof ElementStylesSchema>;
export type ElementTreeDto = z.infer<typeof ElementTreeSchema>;

// Counts the nodes in a tree, so a page that has grown past what a browser can draw is refused at the edge.
export function countElements(nodes: readonly ElementNodeDto[]): number {
  return nodes.reduce((total, node) => total + 1 + countElements(node.children), 0);
}

// Measures how deeply a tree nests, so a page cannot be made to recurse past what the renderer will walk.
export function elementDepth(nodes: readonly ElementNodeDto[]): number {
  return nodes.reduce((deepest, node) => Math.max(deepest, 1 + elementDepth(node.children)), 0);
}

// Reads a stored tree, refusing one that is too deep, too large, or names the same element twice.
export function readElementTree(stored: unknown): ElementTreeDto | null {
  const parsed = ElementTreeSchema.safeParse(stored);
  if (!parsed.success) return null;
  if (countElements(parsed.data.roots) > MAX_ELEMENT_NODES) return null;
  // Measured after parsing, because a prop value is `unknown` and no per-field rule can bound one.
  if (JSON.stringify(parsed.data).length > MAX_ELEMENT_BYTES) return null;
  if (elementDepth(parsed.data.roots) > MAX_ELEMENT_DEPTH) return null;
  const seen = new Set<string>();
  const unique = (nodes: readonly ElementNodeDto[]): boolean =>
    nodes.every((node) => !seen.has(node.id) && Boolean(seen.add(node.id)) && unique(node.children));
  return unique(parsed.data.roots) ? parsed.data : null;
}
